/**
 * WHAT'S ON — the date-led half of the events/venues split.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE PRODUCT SPLIT
 *
 *   WHAT'S ON (this page)  = "what's on tonight / this weekend?"  → DATE-led
 *   PLACES    (Places.tsx) = "where's a pub with a pool table?"   → VENUE-led
 *
 * They were previously one blurred surface: `/events` listed one-off events,
 * `/whats-on` listed venue activities, `/venues` listed venues, and pub
 * *facilities* were smuggled into the event *category* column so that
 * "Karaoke" and "Darts Night" became categories a gala could be filed under.
 * EVENTS_AUDIT.md §2.3 documents all five of the incompatible category
 * vocabularies that produced.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * DATES ARE COMPUTED, NEVER STORED
 *
 * Every preset resolves through `rangeForPreset()` in hub/dates.ts to a
 * concrete inclusive YYYY-MM-DD range derived from the real clock in
 * **Europe/London**, then compared against real `event_date` values. There is
 * no "tonight" flag and no `is_this_weekend` column. `/events/tonight` is a
 * route, not a stored value — it is re-resolved on every render, so a tab left
 * open overnight shows the new day's listings the moment it re-renders.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHERE THE ITEMS COME FROM
 *
 *   GET /api/events    → one-off events, INCLUDING venue-less ones, with images
 *   GET /api/whats-on  → recurring venue activities, expanded to occurrences
 *
 * We take only `type === "activity"` from `/whats-on`. Phase 4 widened that
 * endpoint to return non-venue events too, but those already arrive from
 * `/events` — and only `/events` carries `image_url` and `featured`. See the
 * note on `useVenueActivities` in hub/hub-data.ts.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * EMPTY STATES ARE A FEATURE
 *
 * "No events found" is a dead end. Because facilities now live on the VENUE
 * and dates live on the EVENT, an empty bucket can answer a better question:
 * no live music tonight, but here are the venues with a live music stage, and
 * here is when the next live music actually is. See <NoResults> at the bottom.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY FILTERING IS CLIENT-SIDE
 *
 * Town narrows server-side; date and category narrow in the browser over the
 * fully-loaded upcoming set. That buys truthful counts on every chip *before*
 * the user commits to it ("This weekend (3)"), instant preset switching, and
 * the ability to say "nothing tonight — but 3 things this weekend". None of
 * that is possible if each chip is its own round trip. Volume caveat is
 * documented on `useEvents`.
 */
import { useEffect, useMemo, useState, useRef, Fragment } from "react";
import { Link, useLocation } from "wouter";
import { CalendarDays, CalendarRange, ChevronRight, Plus, Store, X } from "lucide-react";
import { useSEO } from "@/hooks/use-seo";
import { useTown } from "@/hooks/use-town";
import FeedAdSlot from "@/components/FeedAdSlot";
import { useAuth } from "@/hooks/use-auth";
import { useCategories } from "@/hooks/use-categories";
import { useAnalytics } from "@/hooks/use-analytics";
import { QueryState } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  ALL_TOWNS, BROWSE_GRID, CategoryRail, DATE_PRESETS, DATE_PRESET_BY_SLUG,
  ELEVATION, FOCUS_RING, FilterChip, RADII, TAP_TARGET, TYPE, TownPicker,
  VenueMiniCard, WhatsOnCard, WhatsOnCardSkeleton,
  facilitiesForCategory, groupLabel, inRange, londonToday, rangeForPreset,
  recurrenceLabel, resolveFacilities, toISO, townFromSlug, townSlug,
  useEvents, useFacilityDefinitions, useVenueActivities, useVenueFacilityMap,
  useVenues,
  type DatePresetId, type HubEvent, type WhatsOnEntry, type WhatsOnItem,
} from "@/components/hub";

/** Sits directly below the sticky 64px site navbar (Navbar.tsx: `h-16`). */
const STICKY_TOP = "top-16";

const CATEGORY_EMOJI: Record<string, string> = {
  All: "✨", "Live Music": "🎸", "Tribute Acts": "🎤", Karaoke: "🎤",
  "Quiz Night": "❓", Comedy: "😂", Nightlife: "🪩", Family: "👨‍👩‍👧",
  Children: "🧒", Sport: "⚽", Sports: "⚽", Markets: "🛍️", Charity: "💛",
  "Food & Drink": "🍽️", Community: "🤝", Festivals: "🎪", Theatre: "🎭",
  Seasonal: "🎄", Outdoor: "🌳", Fitness: "🏃", Wellbeing: "🧘",
  "Arts & Crafts": "🎨", Workshops: "🛠️", "Darts Night": "🎯", "Pool Night": "🎱",
  "Business & Networking": "💼",
};

/* ── URL ⇄ state ──────────────────────────────────────────────────────────
   The routes are real and shareable:

     /events                        everything upcoming
     /events/tonight                date preset, resolved live
     /events/this-weekend           "
     /events/:town                  town
     /events/:town/:category        town + category
     ?when=<preset>&date=<ISO>      preset alongside a town path
     ?category=<slug>               category without a town

   `/events/:id` (event detail) shares the single-segment shape, so App.tsx
   sends that segment through EventsRoute.tsx: known preset and town slugs land
   here, anything else lands on EventDetail. The 22 SEO-indexed event slugs are
   long titles ("kilmarnock-fair-festival-we-make-kilmarnock"); none collides
   with a town or preset slug. A future event literally named "Ayr" would —
   the town wins, and the event stays reachable at /event/<id>.               */

interface RouteState {
  presetFromPath: DatePresetId | null;
  townSlugFromPath: string | null;
  categorySlugFromPath: string | null;
}

const EMPTY_ROUTE: RouteState = {
  presetFromPath: null, townSlugFromPath: null, categorySlugFromPath: null,
};

function parsePath(pathname: string): RouteState {
  const segments = pathname.split("?")[0]!.split("/").filter(Boolean);
  const [, first, second] = segments; // ["events", first, second]

  if (!first) return EMPTY_ROUTE;
  if (second) {
    return { presetFromPath: null, townSlugFromPath: first, categorySlugFromPath: second };
  }
  const preset = DATE_PRESET_BY_SLUG[first.toLowerCase()];
  if (preset) return { presetFromPath: preset, townSlugFromPath: null, categorySlugFromPath: null };
  return { presetFromPath: null, townSlugFromPath: first, categorySlugFromPath: null };
}

function categorySlug(name: string): string {
  return name.trim().toLowerCase().replace(/\s*[/&]\s*/g, "-").replace(/\s+/g, "-");
}

function buildPath(args: {
  town: string; category: string; preset: DatePresetId; pickedDate: string | null;
}): string {
  const { town, category, preset, pickedDate } = args;
  const hasTown = town !== ALL_TOWNS;
  const hasCategory = category !== "All";

  // The prettiest URL wins where it can express the whole state.
  if (!hasTown && !hasCategory && preset !== "any" && preset !== "pick") {
    const slug = DATE_PRESETS.find((p) => p.id === preset)?.slug;
    if (slug) return `/events/${slug}`;
  }

  const path =
    hasTown && hasCategory ? `/events/${townSlug(town)}/${categorySlug(category)}`
    : hasTown ? `/events/${townSlug(town)}`
    : "/events";

  const query = new URLSearchParams();
  if (preset !== "any") query.set("when", preset);
  if (preset === "pick" && pickedDate) query.set("date", pickedDate);
  if (hasCategory && !hasTown) query.set("category", categorySlug(category));
  const q = query.toString();
  return q ? `${path}?${q}` : path;
}

/* ── Normalisation ────────────────────────────────────────────────────────
   Two record types, one feed shape. The distinction is preserved where it
   matters to the reader (the recurrence badge) and erased where it does not
   (everything else) — an event and a weekly quiz night are the same kind of
   answer to "what can I do on Saturday".                                    */

function eventToEntry(e: HubEvent): WhatsOnEntry {
  const price = (e.price ?? "").trim();
  const isFree = price === "" || price === "0" || price.toLowerCase() === "free";
  return {
    key: `e${e.id}`,
    kind: "event",
    // `GET /events` strips `slug` from list items (events.ts:159), so lists
    // address by id. `/events/:id` resolves either form, and the indexed slug
    // URLs keep working — we simply cannot *link* by slug from a list response.
    href: `/event/${e.id}`,
    title: e.name,
    summary: e.shortSummary ?? e.description ?? null,
    category: e.category || null,
    date: (e.eventDate ?? "").slice(0, 10),
    time: e.eventTime ?? null,
    imageUrl: e.imageUrl ?? null,
    town: e.town ?? null,
    // 4 of 22 rows carry `venue = ''` rather than NULL (audit R12), so trim
    // before deciding whether a venue name exists at all.
    venueName: e.venue?.trim() || null,
    venueHref: e.venueId ? `/venues/${e.venueId}` : null,
    priceText: isFree ? null : price,
    isFree,
    featured: !!e.featured,
    recurrence: null,
  };
}

function activityToEntry(a: WhatsOnItem): WhatsOnEntry {
  const venue = a.venue ?? a.bar;
  // Phase 4 emits `hasVenueProfile`; older builds do not. Both are handled:
  // a synthetic free-text venue carries id 0, which must never become a link.
  const hasVenue = a.hasVenueProfile !== false && !!venue && venue.id > 0;
  const href = hasVenue
    ? (venue!.slug ? `/venue/${venue!.slug}` : `/venues/${venue!.id}`)
    : "/events";
  return {
    key: `a${a.id}`,
    kind: "activity",
    href,
    title: a.title,
    summary: a.description,
    category: a.category || null,
    date: a.date,
    time: a.startTime ?? a.time ?? null,
    imageUrl: venue?.coverPhoto || null,
    town: venue?.town ?? null,
    venueName: a.locationName ?? venue?.name ?? null,
    venueHref: hasVenue ? href : null,
    priceText: a.priceText,
    isFree: a.isFree,
    featured: false,
    recurrence: recurrenceLabel(a.frequency, a.dayOfWeek),
  };
}

/* ── Page ─────────────────────────────────────────────────────────────── */

export default function WhatsOn() {
  const [location, navigate] = useLocation();
  const { track } = useAnalytics();
  const { user } = useAuth();
  const { selectedTown, setSelectedTown, towns } = useTown();

  const route = useMemo(() => parsePath(location), [location]);
  const search = typeof window !== "undefined" ? window.location.search : "";
  const query = useMemo(() => new URLSearchParams(search), [search]);

  const eventCategories = useCategories("event");

  /* Town: a town named in the URL wins; otherwise the app-wide selection from
     the navbar carries over — someone who picked Ayr on Order Food does not
     want to be shown all of Ayrshire here. */
  const townFromUrl = route.townSlugFromPath ? townFromSlug(route.townSlugFromPath, towns) : null;
  const town = townFromUrl ?? selectedTown;
  const previousGlobalTown = useRef(selectedTown);
  useEffect(() => {
    if (previousGlobalTown.current === selectedTown) return;
    previousGlobalTown.current = selectedTown;
    if (townFromUrl && townFromUrl !== selectedTown) apply({ town: selectedTown });
  }, [selectedTown]);


  const [preset, setPreset] = useState<DatePresetId>(
    () => route.presetFromPath ?? (query.get("when") as DatePresetId | null) ?? "any",
  );
  const [pickedDate, setPickedDate] = useState<string | null>(() => query.get("date"));
  const [category, setCategory] = useState<string>("All");

  /* Re-sync on navigation: back button, a link from Home, a shared
     /events/this-weekend URL. */
  useEffect(() => {
    const fromPath = route.presetFromPath ?? (query.get("when") as DatePresetId | null);
    if (fromPath) setPreset(fromPath);
    const d = query.get("date");
    if (d) setPickedDate(d);
    if (townFromUrl && townFromUrl !== selectedTown) setSelectedTown(townFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  /* A category may arrive as a slug from the path or the query string, and can
     only be resolved once the real vocabulary has loaded. */
  useEffect(() => {
    const slug = route.categorySlugFromPath ?? query.get("category");
    if (!slug) return;
    const match = eventCategories.find((c) => categorySlug(c) === slug);
    if (match) setCategory(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, eventCategories.length]);

  useEffect(() => { track("page_viewed", { screen: "whats-on" }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const townParam = town !== ALL_TOWNS ? town : undefined;
  const townLabel = town === ALL_TOWNS ? "Ayrshire" : town;

  useSEO({
    title: `What's On in ${townLabel}`,
    description:
      `What's on tonight, tomorrow and this weekend in ${townLabel} — live music, quiz nights, karaoke, markets, family days and more across Ayrshire.`,
  });

  /* ── Data ───────────────────────────────────────────────────────────── */

  const eventsQuery = useEvents({ town: townParam });
  const activitiesQuery = useVenueActivities({ town: townParam, days: 120 });

  const isLoading = eventsQuery.isLoading || activitiesQuery.isLoading;
  /* Events are the substance of this feed; activities are additive and that
     table holds 0 rows today. Only an events failure is a page failure —
     otherwise a flaky /whats-on would blank a page that has plenty to show. */
  const isError = eventsQuery.isError;

  const todayISO = toISO(londonToday());

  /** Everything upcoming, both sources, sorted by real date then time. */
  const allEntries = useMemo(() => {
    const events = (eventsQuery.data?.items ?? []).map(eventToEntry);
    const activities = activitiesQuery.activities.map(activityToEntry);
    return [...events, ...activities]
      .filter((e) => e.date && e.date >= todayISO)
      .sort((a, b) =>
        a.date === b.date
          ? (a.time ?? "99:99").localeCompare(b.time ?? "99:99")
          : a.date.localeCompare(b.date),
      );
  }, [eventsQuery.data, activitiesQuery.activities, todayISO]);

  /* Chips come from the vocabulary AND from what is actually in the feed. The
     audit found 3 live category values (Karaoke, Quiz Night, Darts Night) that
     exist in no table at all — without this union those events would be
     unreachable by any chip on the page. Categories with nothing in them are
     dropped, so no chip ever leads to an empty screen. */
  const categories = useMemo(() => {
    const present = new Set(allEntries.map((e) => e.category).filter(Boolean) as string[]);
    // `categories(type='event')` holds 32 rows for ~21 real categories — 8 names
    // are duplicated outright (Comedy 32/84, Family 28/76, Charity 29/78 …,
    // audit R9). Without this dedupe the rail renders "Comedy" twice and React
    // warns about duplicate keys. Deduping here is presentation-only; fixing the
    // table is MIGRATION_REPORT §7's job.
    const ordered = [...new Set(eventCategories)].filter((c) => present.has(c));
    const orphans = [...present].filter((c) => !eventCategories.includes(c)).sort();
    return ["All", ...ordered, ...orphans];
  }, [allEntries, eventCategories]);

  const categoryFiltered = useMemo(
    () => (category === "All" ? allEntries : allEntries.filter((e) => e.category === category)),
    [allEntries, category],
  );

  const dateRange = useMemo(() => rangeForPreset(preset, pickedDate), [preset, pickedDate]);
  const visible = useMemo(
    () => categoryFiltered.filter((e) => inRange(e.date, dateRange)),
    [categoryFiltered, dateRange],
  );

  /** Truthful counts on every chip, over the category-narrowed set. */
  const presetCounts = useMemo(() => {
    const out: Partial<Record<DatePresetId, number>> = {};
    for (const p of DATE_PRESETS) {
      if (p.id === "pick") continue;
      const range = rangeForPreset(p.id, null);
      out[p.id] = categoryFiltered.filter((e) => inRange(e.date, range)).length;
    }
    return out;
  }, [categoryFiltered]);

  /** Date sections — "Tonight", "Tomorrow", "Saturday 29 August". */
  const sections = useMemo(() => {
    const map = new Map<string, WhatsOnEntry[]>();
    for (const entry of visible) {
      const bucket = map.get(entry.date);
      if (bucket) bucket.push(entry);
      else map.set(entry.date, [entry]);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [visible]);

  /* ── Interaction ────────────────────────────────────────────────────── */

  function apply(next: Partial<{
    town: string; category: string; preset: DatePresetId; pickedDate: string | null;
  }>) {
    const nextTown = next.town ?? town;
    const nextCategory = next.category ?? category;
    const nextPreset = next.preset ?? preset;
    const nextDate = next.pickedDate !== undefined ? next.pickedDate : pickedDate;

    if (next.town !== undefined) setSelectedTown(next.town);
    if (next.category !== undefined) setCategory(next.category);
    if (next.preset !== undefined) setPreset(next.preset);
    if (next.pickedDate !== undefined) setPickedDate(next.pickedDate);

    // `replace` so filtering does not fill the back stack with 12 near-identical
    // URLs — Back should return to wherever the user came from.
    navigate(
      buildPath({ town: nextTown, category: nextCategory, preset: nextPreset, pickedDate: nextDate }),
      { replace: true },
    );
  }

  const hasFilter = preset !== "any" || category !== "All" || town !== ALL_TOWNS;
  const activePreset = DATE_PRESETS.find((p) => p.id === preset);
  const presetLabel =
    preset === "pick" && pickedDate ? groupLabel(pickedDate).toLowerCase()
    : preset === "any" ? ""
    : (activePreset?.label ?? "").toLowerCase();

  const skeleton = (
    <div className={BROWSE_GRID}>
      {Array.from({ length: 8 }).map((_, i) => <WhatsOnCardSkeleton key={i} />)}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Controls ────────────────────────────────────────────────────
          Three compact rows, then results. Deliberately not a hero: on a
          390px phone the first card must be on screen without scrolling —
          the standard the Order Food redesign was held to (65% → 37% chrome).
          The old page spent a 10rem navy hero, a full-width owner banner and
          a three-field white filter card before showing anything. */}
      <div className="bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 pt-3 pb-2">
          {/* Row 1 — identity and actions */}
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className={cn(TYPE.footnote, "text-slate-500 hidden sm:block")}>
                Events, gigs and pub nights across Ayrshire
              </p>
              <h1 className={cn(TYPE.title1, "text-[#1e3a5f]")}>What's On</h1>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <Link
                href="/places"
                className={cn(
                  TYPE.caption, RADII.pill, FOCUS_RING,
                  "hidden lg:inline-flex items-center gap-1.5 h-8 px-3 border border-slate-200 text-slate-600",
                  "hover:border-slate-300 hover:text-slate-900 transition-colors",
                )}
              >
                <Store size={13} aria-hidden /> Browse Places
              </Link>
              {user && (
                <Link href={user.role === "business" ? "/business/events" : "/submit-event"}>
                  <Button size="sm" className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90">
                    <Plus size={14} className="mr-1" aria-hidden />
                    Add event
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Row 2 — DATES. The primary control on a date-led page, so it gets
              its own row, and the counts on it are real. */}
          <div
            role="group"
            aria-label="Filter by date"
            // `relative` is load-bearing, not decoration. Without it the row's
            // overflowing chips still counted towards <html>'s scroll width —
            // body stayed 375px but the document reported 626px, so the phone
            // zoomed the whole page out to fit. Establishing a containing block
            // here stops that, and is what the shared ScrollRow already does,
            // which is why rails built on it were never affected.
            className="relative flex items-center gap-2 mt-3 py-0.5 overflow-x-auto scrollbar-none"
          >
            {DATE_PRESETS.filter((p) => p.id !== "pick").map((p) => {
              const count = presetCounts[p.id] ?? 0;
              const active = preset === p.id;
              const dim = !active && count === 0 && p.id !== "any";
              return (
                <button
                  key={p.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => apply({ preset: p.id, pickedDate: null })}
                  className={cn(
                    TYPE.caption, RADII.pill, FOCUS_RING, TAP_TARGET,
                    "inline-flex shrink-0 items-center gap-1.5 px-3.5 h-9 border transition-colors cursor-pointer whitespace-nowrap",
                    active ? "bg-[#1e3a5f] text-white border-[#1e3a5f]"
                    : dim ? "bg-white text-slate-400 border-slate-200 hover:border-slate-300"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900",
                  )}
                >
                  {p.id === "any" && <CalendarDays size={13} aria-hidden />}
                  {(p.id === "this-weekend" || p.id === "next-weekend") && <CalendarRange size={13} aria-hidden />}
                  {p.label}
                  {p.id !== "any" && (
                    <span className={active ? "text-white/70" : "text-slate-400"}>{count}</span>
                  )}
                </button>
              );
            })}

            {/* Pick a date — a real date input, not another bucket. */}
            <label
              className={cn(
                TYPE.caption, RADII.pill, FOCUS_RING, TAP_TARGET,
                "inline-flex shrink-0 items-center gap-1.5 pl-3 pr-2 h-9 border transition-colors cursor-pointer",
                preset === "pick" && pickedDate
                  ? "bg-[#1e3a5f] text-white border-[#1e3a5f]"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300",
              )}
            >
              <span className="sr-only">Pick a date</span>
              <input
                type="date"
                min={todayISO}
                value={pickedDate ?? ""}
                onChange={(e) =>
                  apply(e.target.value
                    ? { preset: "pick", pickedDate: e.target.value }
                    : { preset: "any", pickedDate: null })
                }
                className="bg-transparent border-0 outline-none cursor-pointer text-inherit w-[7.5rem] p-0"
              />
            </label>
          </div>
        </div>

        {/* Row 3 — category rail + town. Sticks under the site navbar so the
            user can re-filter from any scroll depth, as the app's rail pins. */}
        <div className={cn("sticky z-30 bg-white/95 backdrop-blur border-t border-slate-100", STICKY_TOP)}>
          <div className="container mx-auto px-4 flex items-center gap-2">
            <CategoryRail
              className="min-w-0 flex-1"
              items={categories}
              selected={category}
              onSelect={(c) => apply({ category: c })}
              emoji={CATEGORY_EMOJI}
            />
            <TownPicker value={town} onChange={(t) => apply({ town: t })} />
          </div>
        </div>
      </div>

      {/* ── Feed ────────────────────────────────────────────────────────── */}
      <div className="container mx-auto px-4 py-4 sm:py-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <p className={cn(TYPE.footnote, "text-slate-500")}>
            {isLoading
              ? "Finding what's on…"
              : `${visible.length} ${visible.length === 1 ? "thing" : "things"} on${presetLabel ? ` ${presetLabel}` : ""} in ${townLabel}`}
          </p>
          {hasFilter && (
            <FilterChip
              icon={<X size={12} aria-hidden />}
              onClick={() => apply({ preset: "any", category: "All", town: ALL_TOWNS, pickedDate: null })}
            >
              Clear
            </FilterChip>
          )}
        </div>

        <QueryState
          isLoading={isLoading}
          isError={isError}
          isEmpty={visible.length === 0}
          onRetry={eventsQuery.refetch}
          skeleton={skeleton}
          errorTitle="Couldn't load what's on"
          errorBody="We couldn't reach Ayrshire Hub just now. Check your connection and try again."
          empty={
            <NoResults
              preset={preset}
              presetLabel={presetLabel}
              category={category}
              town={town}
              townLabel={townLabel}
              allEntries={allEntries}
              categoryFiltered={categoryFiltered}
              presetCounts={presetCounts}
              onApply={apply}
            />
          }
        >
          <div className="space-y-7">
            {sections.map(([date, entries], si) => (
              <Fragment key={date}>
                <section>
                  <div className="flex items-center gap-3 mb-3">
                    <h2 className={cn(TYPE.title3, "text-slate-900")}>{groupLabel(date)}</h2>
                    <span className="h-px flex-1 bg-slate-200" />
                    <span className={cn(TYPE.footnote, "text-slate-400")}>
                      {entries.length} {entries.length === 1 ? "listing" : "listings"}
                    </span>
                  </div>
                  <div className={BROWSE_GRID}>
                    {entries.map((entry) => <WhatsOnCard key={entry.key} entry={entry} />)}
                  </div>
                </section>
                {/* Weave an advert between date sections so it reads as part of the feed. */}
                {(si + 1) % 2 === 0 && si < sections.length - 1 && (
                  <FeedAdSlot placement="events" town={town && town !== "All Towns" ? town : undefined} index={Math.floor(si / 2)} />
                )}
              </Fragment>
            ))}
          </div>
        </QueryState>

        {/* Cross-sell after the feed, where it cannot delay the first card. */}
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <Link
            href="/places"
            className={cn(
              RADII.md, FOCUS_RING,
              "flex items-center gap-3 border border-slate-200 bg-white px-4 py-3 transition-colors hover:border-slate-300",
            )}
          >
            <span className="text-xl" aria-hidden>🍺</span>
            <span className="flex-1 min-w-0">
              <span className={cn(TYPE.bodyStrong, "block text-slate-900")}>Looking for a place, not a date?</span>
              <span className={cn(TYPE.footnote, "block text-slate-500")}>
                Find pubs with a pool table, darts, live sport or a beer garden
              </span>
            </span>
            <ChevronRight size={16} className="text-slate-400 shrink-0" aria-hidden />
          </Link>

          <Link
            href="/list-your-venue"
            className={cn(
              RADII.md, FOCUS_RING,
              "flex items-center gap-3 border border-amber-200 bg-amber-50 px-4 py-3 transition-colors hover:bg-amber-100",
            )}
          >
            <span className="text-xl" aria-hidden>📣</span>
            <span className="flex-1 min-w-0">
              <span className={cn(TYPE.bodyStrong, "block text-amber-900")}>Run a pub, bar or club?</span>
              <span className={cn(TYPE.footnote, "block text-amber-800/80")}>
                List your venue free — add your regular nights once, not every week
              </span>
            </span>
            <ChevronRight size={16} className="text-amber-700 shrink-0" aria-hidden />
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ── Empty state ──────────────────────────────────────────────────────────
   This is the payoff for separating events from venues.

   Three answers, best first, and every one is a real query:

     1. "Venues that regularly have live music" — resolved from
        `venue_facilities`, i.e. from the VENUE. Only expressible because the
        facility has stopped pretending to be an event category.
     2. "Not tonight — but here is when it actually is", with real dates.
     3. "Nothing in Live Music this weekend — here's everything on this
        weekend" (drop the category, keep the date).

   The hooks for (1) live inside this component, so the venue and facility
   requests only fire when the feed is genuinely empty.                       */

function NoResults({
  preset, presetLabel, category, town, townLabel,
  allEntries, categoryFiltered, presetCounts, onApply,
}: {
  preset: DatePresetId;
  presetLabel: string;
  category: string;
  town: string;
  townLabel: string;
  allEntries: WhatsOnEntry[];
  categoryFiltered: WhatsOnEntry[];
  presetCounts: Partial<Record<DatePresetId, number>>;
  onApply: (next: Partial<{
    town: string; category: string; preset: DatePresetId; pickedDate: string | null;
  }>) => void;
}) {
  const facilitySlugs = facilitiesForCategory(category);
  const wantsVenues = facilitySlugs.length > 0;

  /* Deliberately NOT town-scoped: if there is no live music in Troon, the
     honest answer still includes the Kilmarnock venues that have a stage. */
  const venuesQuery = useVenues({});
  const venues = useMemo(() => venuesQuery.data?.items ?? [], [venuesQuery.data]);
  const { facilityMap } = useVenueFacilityMap(wantsVenues ? venues : []);
  const { data: defsData } = useFacilityDefinitions();
  const definitions = defsData?.items ?? [];

  const matchingVenues = useMemo(() => {
    if (!wantsVenues) return [];
    return venues.filter((v) => {
      const slugs = facilityMap.get(v.id);
      return !!slugs && facilitySlugs.some((s) => slugs.has(s));
    });
  }, [venues, facilityMap, facilitySlugs, wantsVenues]);

  /* Same dates, category dropped. */
  const sameDateAnyCategory = useMemo(() => {
    if (category === "All") return [];
    const range = rangeForPreset(preset, null);
    return allEntries.filter((e) => inRange(e.date, range));
  }, [allEntries, category, preset]);

  /* Same category, date dropped — the genuine "next up". */
  const nextUp = useMemo(() => categoryFiltered.slice(0, 4), [categoryFiltered]);

  const whenPhrase = presetLabel ? ` ${presetLabel}` : "";
  const whatPhrase = category === "All" ? "on" : `${category.toLowerCase()} on`;
  const lowerCategory = category.toLowerCase();

  const otherPresets = DATE_PRESETS.filter(
    (p) => p.id !== preset && p.id !== "pick" && p.id !== "any" && (presetCounts[p.id] ?? 0) > 0,
  );

  return (
    <div className="space-y-6">
      <div className={cn(RADII.md, ELEVATION.sm, "bg-white border border-slate-200/80 p-5 sm:p-6")}>
        <p className={cn(TYPE.title3, "text-slate-900")}>
          Nothing {whatPhrase}{whenPhrase}{town !== ALL_TOWNS ? ` in ${townLabel}` : ""}.
        </p>
        <p className={cn(TYPE.callout, "text-slate-500 mt-1")}>
          {otherPresets.length > 0 || matchingVenues.length > 0
            ? "Here's what we can tell you instead."
            : "Check back soon — venues add listings all the time."}
        </p>

        <div className="flex flex-wrap gap-2 mt-4">
          {otherPresets.map((p) => (
            <FilterChip key={p.id} onClick={() => onApply({ preset: p.id, pickedDate: null })}>
              {p.label} ({presetCounts[p.id]})
            </FilterChip>
          ))}
          {category !== "All" && sameDateAnyCategory.length > 0 && (
            <FilterChip onClick={() => onApply({ category: "All" })}>
              Everything{whenPhrase} ({sameDateAnyCategory.length})
            </FilterChip>
          )}
          {town !== ALL_TOWNS && (
            <FilterChip onClick={() => onApply({ town: ALL_TOWNS })}>All of Ayrshire</FilterChip>
          )}
        </div>
      </div>

      {/* (1) The venue-led answer the old "No events found" could never give. */}
      {matchingVenues.length > 0 && (
        <section>
          <h2 className={cn(TYPE.title3, "text-slate-900 mb-1")}>
            Venues that regularly have {lowerCategory}
          </h2>
          <p className={cn(TYPE.footnote, "text-slate-500 mb-3")}>
            Nothing is listed{whenPhrase}, but these places have the kit for it — worth a look.
          </p>
          <div className="grid gap-2 sm:grid-cols-2 min-[1000px]:grid-cols-3">
            {matchingVenues.map((v) => (
              <VenueMiniCard
                key={v.id}
                venue={v}
                facilities={resolveFacilities(facilityMap.get(v.id) ?? [], definitions)}
              />
            ))}
          </div>
        </section>
      )}

      {/* (2) Real dates for the thing they actually asked for. */}
      {nextUp.length > 0 && (
        <section>
          <h2 className={cn(TYPE.title3, "text-slate-900 mb-1")}>
            {category === "All" ? "Coming up" : `Next ${lowerCategory}`}
          </h2>
          <p className={cn(TYPE.footnote, "text-slate-500 mb-3")}>
            Not {presetLabel || "today"} — but here is when it actually is.
          </p>
          <div className={BROWSE_GRID}>
            {nextUp.map((entry) => <WhatsOnCard key={entry.key} entry={entry} />)}
          </div>
        </section>
      )}

      {/* (3) Same dates, wider net. */}
      {category !== "All" && sameDateAnyCategory.length > 0 && (
        <section>
          <h2 className={cn(TYPE.title3, "text-slate-900 mb-1")}>Everything else{whenPhrase}</h2>
          <div className={BROWSE_GRID}>
            {sameDateAnyCategory.slice(0, 8).map((entry) => (
              <WhatsOnCard key={entry.key} entry={entry} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
